import { TILE } from '../../sim/constants';
import { T_ONEWAY, T_SOLID, type Room } from '../../sim/room';
import { JUNGLE, mix } from './palette';
import type { SpriteSheet } from './sprites';

/**
 * Art drawn in code (as lawnmower does): every texture is painted onto a canvas at boot, so the
 * prototype needs no image files and a palette change is one edit. All drawing is whole pixels.
 */

type Ctx = CanvasRenderingContext2D;

/** A stable pseudo-random number in [0, 1) for a position: the same pebble lands in the same place. */
export function hash(x: number, y: number, salt = 0): number {
  let h = (x * 374761393 + y * 668265263 + salt * 2147483647) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

export function px(ctx: Ctx, color: string, x: number, y: number, w = 1, h = 1): void {
  ctx.fillStyle = color;
  ctx.fillRect(x, y, w, h);
}

/** Paints every frame of a sheet side by side, starting at (0, 0). */
export function paintSheet(ctx: Ctx, sheet: SpriteSheet): void {
  sheet.frames.forEach((frame, f) => {
    frame.forEach((row, y) => {
      [...row].forEach((ch, x) => {
        const color = sheet.palette[ch];
        if (color) px(ctx, color, f * sheet.width + x, y);
      });
    });
  });
}

/** Extra world around the room drawn on each lane, so zoomed-out lanes never show an edge. */
export const LANE_PAD_X = 320;
export const LANE_PAD_BOTTOM = 96;

/** How a world's ground is drawn: its colours, a grassy or a dusty top, wooden or metal planks. */
export interface GroundLook {
  readonly cap: 'grass' | 'dust';
  readonly plank: 'wood' | 'metal';
  readonly colors: {
    readonly top: string;
    readonly topLit: string;
    readonly topDark: string;
    readonly body: string;
    readonly bodyDark: string;
    readonly speck: string;
    readonly ink: string;
    readonly plank: string;
    readonly plankLit: string;
    readonly plankDark: string;
    /** Vines hanging under wooden planks (none on airless worlds). */
    readonly hang: string | null;
  };
}

export const JUNGLE_GROUND: GroundLook = {
  cap: 'grass',
  plank: 'wood',
  colors: {
    top: JUNGLE.grass,
    topLit: JUNGLE.grassLit,
    topDark: JUNGLE.grassDark,
    body: JUNGLE.dirt,
    bodyDark: JUNGLE.dirtDark,
    speck: JUNGLE.pebble,
    ink: JUNGLE.ink,
    plank: JUNGLE.plank,
    plankLit: JUNGLE.plankLit,
    plankDark: JUNGLE.plankDark,
    hang: JUNGLE.leaf,
  },
};

type Colors = GroundLook['colors'];

/**
 * One lane's tiles, mixed toward the haze colour by `haze` (0 = near, crisp; higher = farther).
 * Outside the room the edge columns and the bottom row carry on, so the ground never ends.
 */
export function paintLane(
  ctx: Ctx,
  room: Room,
  lane: number,
  haze: number,
  look: GroundLook,
  hazeColor: string,
): void {
  const c = (color: string) => mix(color, hazeColor, haze);
  const k = look.colors;
  const colors: Colors = {
    top: c(k.top),
    topLit: c(k.topLit),
    topDark: c(k.topDark),
    body: c(k.body),
    bodyDark: c(k.bodyDark),
    speck: c(k.speck),
    ink: c(k.ink),
    plank: c(k.plank),
    plankLit: c(k.plankLit),
    plankDark: c(k.plankDark),
    hang: k.hang === null ? null : c(k.hang),
  };
  const codes = room.tiles[lane];
  if (!codes) return;
  const codeAt = (tx: number, ty: number): number => {
    if (ty < 0) return 0;
    const cx = Math.min(room.cols - 1, Math.max(0, tx));
    const cy = Math.min(room.rows - 1, ty);
    return codes[cy * room.cols + cx] ?? 0;
  };
  const padTiles = LANE_PAD_X / TILE;
  const rows = room.rows + LANE_PAD_BOTTOM / TILE;
  for (let ty = 0; ty < rows; ty++) {
    for (let tx = -padTiles; tx < room.cols + padTiles; tx++) {
      const t = codeAt(tx, ty);
      const x = (tx + padTiles) * TILE;
      const y = ty * TILE;
      if (t === T_SOLID) {
        paintGround(ctx, colors, look.cap, x, y, tx, ty, {
          up: codeAt(tx, ty - 1) === T_SOLID,
          left: codeAt(tx - 1, ty) === T_SOLID,
          right: codeAt(tx + 1, ty) === T_SOLID,
          down: codeAt(tx, ty + 1) === T_SOLID,
        });
      } else if (t === T_ONEWAY) {
        const n = { left: codeAt(tx - 1, ty) === T_ONEWAY, right: codeAt(tx + 1, ty) === T_ONEWAY };
        if (look.plank === 'wood') paintPlank(ctx, colors, x, y, tx, ty, n);
        else paintGirder(ctx, colors, x, y, n);
      }
    }
  }
  const vents = room.vents.filter((v) => v.lane === lane);
  const ventAt = (tx: number, ty: number) => vents.some((v) => v.tx === tx && v.bottom === ty);
  for (const v of vents) {
    paintVent(ctx, colors, (v.tx + padTiles) * TILE, v.bottom * TILE, {
      left: ventAt(v.tx - 1, v.bottom),
      right: ventAt(v.tx + 1, v.bottom),
    });
  }
}

/**
 * A dust vent at the bottom of its tile: a dark mouth with a lip of rock, lit on top, at each
 * outer end (vents side by side share one long mouth).
 */
function paintVent(
  ctx: Ctx,
  k: Colors,
  x: number,
  y: number,
  n: { left: boolean; right: boolean },
): void {
  const x0 = n.left ? x : x + 4;
  const x1 = n.right ? x + TILE : x + TILE - 4;
  px(ctx, k.ink, x0, y + TILE - 3, x1 - x0, 3);
  for (const [open, lx] of [
    [n.left, x],
    [n.right, x + TILE - 4],
  ] as const) {
    if (open) continue;
    px(ctx, k.bodyDark, lx, y + TILE - 6, 4, 6);
    px(ctx, k.topLit, lx, y + TILE - 7, 4, 1);
    px(ctx, k.ink, lx === x ? x + 3 : lx, y + TILE - 6, 1, 6);
  }
}

interface Neighbours {
  up: boolean;
  left: boolean;
  right: boolean;
  down: boolean;
}

function paintGround(
  ctx: Ctx,
  k: Colors,
  cap: GroundLook['cap'],
  x: number,
  y: number,
  tx: number,
  ty: number,
  n: Neighbours,
): void {
  px(ctx, k.body, x, y, TILE, TILE);
  // Pebbles and darker clods.
  for (let i = 0; i < 4; i++) {
    const r = hash(tx, ty, i);
    const ox = Math.floor(hash(tx, ty, i + 10) * 14);
    const oy = Math.floor(hash(tx, ty, i + 20) * 14);
    if (r < 0.35) px(ctx, k.speck, x + ox, y + oy, 2, 1);
    else if (r < 0.7) px(ctx, k.bodyDark, x + ox, y + oy, 2, 2);
  }
  if (!n.left) {
    px(ctx, k.ink, x, y, 1, TILE);
    px(ctx, k.bodyDark, x + 1, y, 1, TILE);
  }
  if (!n.right) {
    px(ctx, k.ink, x + TILE - 1, y, 1, TILE);
    px(ctx, k.bodyDark, x + TILE - 2, y, 1, TILE);
  }
  if (!n.down) {
    px(ctx, k.bodyDark, x, y + TILE - 3, TILE, 2);
    px(ctx, k.ink, x, y + TILE - 1, TILE, 1);
  }
  if (n.up) return;
  if (cap === 'grass') {
    // Grass cap: a dark base, the body, a lit top row, tufts above and drips below.
    px(ctx, k.topDark, x, y, TILE, 6);
    px(ctx, k.top, x, y, TILE, 4);
    px(ctx, k.topLit, x, y, TILE, 1);
    for (let i = 0; i < TILE; i++) {
      const r = hash(tx * TILE + i, ty, 3);
      if (r < 0.28) px(ctx, k.top, x + i, y - 1);
      if (r < 0.1) px(ctx, k.topLit, x + i, y - 2);
      if (r > 0.82) px(ctx, k.topDark, x + i, y + 6, 1, 1 + Math.floor(r * 3));
    }
  } else {
    // Dust cap: a pale crust with small pits in it and loose grains lying on top.
    px(ctx, k.topDark, x, y, TILE, 5);
    px(ctx, k.top, x, y, TILE, 3);
    px(ctx, k.topLit, x, y, TILE, 1);
    for (let i = 0; i < TILE; i++) {
      const r = hash(tx * TILE + i, ty, 5);
      if (r < 0.12) px(ctx, k.topDark, x + i, y + 1, 2, 1);
      if (r > 0.9) px(ctx, k.top, x + i, y - 1);
    }
  }
  if (!n.left) px(ctx, k.ink, x, y, 1, 6);
  if (!n.right) px(ctx, k.ink, x + TILE - 1, y, 1, 6);
}

function paintPlank(
  ctx: Ctx,
  k: Colors,
  x: number,
  y: number,
  tx: number,
  ty: number,
  n: { left: boolean; right: boolean },
): void {
  px(ctx, k.plankLit, x, y, TILE, 1);
  px(ctx, k.plank, x, y + 1, TILE, 3);
  px(ctx, k.plankDark, x, y + 4, TILE, 1);
  px(ctx, k.ink, x, y + 5, TILE, 1);
  if (!n.left) px(ctx, k.ink, x, y, 1, 6);
  if (!n.right) px(ctx, k.ink, x + TILE - 1, y, 1, 6);
  // Lashings and hanging vines.
  px(ctx, k.plankDark, x + 7, y + 1, 1, 3);
  if (!k.hang) return;
  for (let i = 0; i < TILE; i += 3) {
    const r = hash(tx * TILE + i, ty, 7);
    if (r < 0.35) px(ctx, k.hang, x + i, y + 6, 1, 2 + Math.floor(r * 18));
  }
}

/** A metal girder (airless worlds): a lit rail, cross-bracing, rivets, a dark lower rail. */
function paintGirder(
  ctx: Ctx,
  k: Colors,
  x: number,
  y: number,
  n: { left: boolean; right: boolean },
): void {
  px(ctx, k.plankLit, x, y, TILE, 1);
  px(ctx, k.plank, x, y + 1, TILE, 2);
  px(ctx, k.plankDark, x, y + 6, TILE, 2);
  for (let i = 0; i < 4; i++) {
    // Cross-bracing between the rails: a zigzag.
    px(ctx, k.plankDark, x + i, y + 3 + i, 1, 1);
    px(ctx, k.plankDark, x + 7 - i, y + 3 + i, 1, 1);
    px(ctx, k.plankDark, x + 8 + i, y + 3 + i, 1, 1);
    px(ctx, k.plankDark, x + 15 - i, y + 3 + i, 1, 1);
  }
  px(ctx, k.plankLit, x + 3, y + 1);
  px(ctx, k.plankLit, x + 11, y + 1);
  px(ctx, k.ink, x, y + 8, TILE, 1);
  if (!n.left) px(ctx, k.ink, x, y, 1, 9);
  if (!n.right) px(ctx, k.ink, x + TILE - 1, y, 1, 9);
}

/** A sky that fills the screen: stepped bands (no smooth gradient, in keeping with pixel art). */
export function paintSky(ctx: Ctx, width: number, height: number): void {
  paintBands(ctx, JUNGLE.skyBands, width, height);
  for (let i = 0; i < 90; i++) {
    const x = Math.floor(hash(i, 1) * width);
    const y = Math.floor(hash(i, 2) * height * 0.55);
    px(ctx, hash(i, 3) < 0.2 ? JUNGLE.glow : JUNGLE.star, x, y);
  }
  // Two moons: a big pale one, a small teal one.
  // Fixed positions so both moons show on the narrowest (480 px) screen.
  const moonX = Math.min(width - 60, 372);
  disc(ctx, moonX, 52, 22, JUNGLE.moonBig);
  disc(ctx, moonX + 7, 52 - 4, 17, JUNGLE.moonBigShade);
  disc(ctx, moonX + 4, 52 - 2, 17, JUNGLE.moonBig);
  disc(ctx, 104, 36, 7, JUNGLE.moonSmall);
}

/** Stepped sky bands down the height, each seam dithered into the next (no smooth gradient). */
export function paintBands(
  ctx: Ctx,
  bands: readonly string[],
  width: number,
  height: number,
): void {
  const bandH = Math.ceil(height / bands.length);
  bands.forEach((color, i) => {
    px(ctx, color, 0, i * bandH, width, bandH);
    const next = bands[i + 1];
    if (next) for (let x = 0; x < width; x += 2) px(ctx, next, x + (i % 2), (i + 1) * bandH - 1);
  });
}

/**
 * A dark mound rising from the bottom edge (a foreground rock): a half-disc `stretch` times wider
 * than tall, with a lit streak near its top.
 */
export function paintMound(
  ctx: Ctx,
  cx: number,
  r: number,
  stretch: number,
  height: number,
  fill: string,
  lit: string,
): void {
  for (let y = -r; y <= 0; y++) {
    const half = Math.floor(Math.sqrt(r * r - y * y) * stretch);
    px(ctx, fill, cx - half, height + y, half * 2 + 1, 1);
  }
  px(ctx, lit, cx - Math.floor(r * 0.5), height - r + 2, Math.floor(r * 0.6), 1);
}

export function disc(ctx: Ctx, cx: number, cy: number, r: number, color: string): void {
  ctx.fillStyle = color;
  for (let y = -r; y <= r; y++) {
    const half = Math.floor(Math.sqrt(r * r - y * y));
    ctx.fillRect(Math.round(cx - half), Math.round(cy + y), half * 2 + 1, 1);
  }
}

/** Repeating mountain ridges. Periods divide the width so the texture tiles seamlessly. */
export function paintMountains(ctx: Ctx, width: number, height: number): void {
  const ridge = (x: number, base: number, amp: number, phase: number) => {
    const t = (x / width) * Math.PI * 2;
    return Math.round(
      base -
        amp *
          (0.55 * Math.sin(t + phase) +
            0.3 * Math.sin(t * 3 + phase * 2) +
            0.15 * Math.sin(t * 7 + phase * 3)),
    );
  };
  const far = mix(JUNGLE.mountain, JUNGLE.haze, 0.35);
  const farLit = mix(JUNGLE.mountainLit, JUNGLE.haze, 0.35);
  for (let x = 0; x < width; x++) {
    const top = ridge(x, 150, 46, 0.6);
    px(ctx, far, x, top, 1, height - top);
    if (ridge(x - 1, 150, 46, 0.6) > top) px(ctx, farLit, x, top, 1, 3);
  }
  for (let x = 0; x < width; x++) {
    const top = ridge(x, 188, 26, 2.1);
    px(ctx, JUNGLE.mountainNear, x, top, 1, height - top);
    if (ridge(x - 1, 188, 26, 2.1) > top) px(ctx, JUNGLE.mountainNearLit, x, top, 1, 2);
  }
}

/** Giant trees between the mountains and the lanes: trunks, canopy, vines, undergrowth. */
export function paintCanopy(ctx: Ctx, width: number, height: number): void {
  const trunks = [
    { x: 40, w: 22 },
    { x: 172, w: 30 },
    { x: 300, w: 18 },
  ];
  for (const t of trunks) {
    px(ctx, JUNGLE.trunk, t.x, 0, t.w, height);
    px(ctx, JUNGLE.trunkLit, t.x, 0, 3, height);
    for (let y = 0; y < height; y += 9) {
      if (hash(t.x, y) < 0.5)
        px(ctx, JUNGLE.trunkLit, t.x + 4 + Math.floor(hash(t.x, y, 1) * (t.w - 8)), y, 2, 4);
    }
    // Roots flaring out at the base.
    for (let i = 0; i < 14; i++)
      px(ctx, JUNGLE.trunk, t.x - i, height - 60 + i * 3, t.w + i * 2, 3);
  }
  // Canopy blobs across the top, drawn twice where they cross the seam.
  for (let i = 0; i < 26; i++) {
    const cx = Math.floor(hash(i, 4) * width);
    const cy = Math.floor(hash(i, 5) * 34);
    const r = 14 + Math.floor(hash(i, 6) * 18);
    for (const off of [-width, 0, width]) {
      disc(ctx, cx + off, cy, r, JUNGLE.canopy);
      disc(ctx, cx + off - 3, cy - 3, r - 6, JUNGLE.canopyLit);
    }
  }
  for (let i = 0; i < 22; i++) {
    const x = Math.floor(hash(i, 8) * width);
    const len = 30 + Math.floor(hash(i, 9) * 90);
    px(ctx, JUNGLE.vine, x, 20, 1, len);
    px(ctx, JUNGLE.canopyLit, x - 1, 20 + len, 3, 2);
  }
  // Undergrowth band along the bottom.
  for (let x = 0; x < width; x++) {
    const top = height - 46 + Math.round(6 * Math.sin((x / width) * Math.PI * 8) + 4 * hash(x, 11));
    px(ctx, JUNGLE.undergrowth, x, top, 1, height - top);
  }
}

/** Dark silhouettes that pass in front of the player: hanging leaves, a vine, a fern. */
export function paintForeground(ctx: Ctx, width: number, height: number): void {
  // Hanging leaf cluster.
  for (let i = 0; i < 7; i++) {
    const x = 90 + i * 9;
    const len = 30 + Math.floor(hash(i, 12) * 34);
    leaf(ctx, x, -4, len, i % 2 === 0 ? 1 : -1);
  }
  // A long vine with leaves.
  const vineX = Math.round(width * 0.73);
  px(ctx, JUNGLE.fg, vineX, 0, 2, 140);
  for (let y = 16; y < 140; y += 18) leaf(ctx, vineX + (y % 36 === 16 ? 2 : -10), y, 12, 1);
  // A fern rising from the bottom.
  for (let i = 0; i < 9; i++) {
    const x = Math.round(width * 0.47) + i * 6;
    const h = 30 + Math.floor(hash(i, 14) * 40);
    px(ctx, JUNGLE.fg, x, height - h, 3, h);
    px(ctx, JUNGLE.fgLit, x, height - h, 1, Math.floor(h / 2));
  }
}

function leaf(ctx: Ctx, x: number, y: number, len: number, lean: 1 | -1): void {
  for (let i = 0; i < len; i++) {
    const w = Math.max(1, Math.round(Math.sin((i / len) * Math.PI) * 5));
    const ox = Math.round((i / len) * 6 * lean);
    px(ctx, JUNGLE.fg, x + ox - Math.floor(w / 2), y + i, w, 1);
    if (i % 4 === 0) px(ctx, JUNGLE.fgLit, x + ox, y + i, 1, 1);
  }
}
